import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Tooltip, IconButton, Typography, Chip } from "@material-ui/core";
import DirectionsBusIcon from "@material-ui/icons/DirectionsBus";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import LocationOnOutlinedIcon from "@material-ui/icons/LocationOnOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import BatteryFullIcon from "@material-ui/icons/BatteryFull";
import Battery60Icon from "@material-ui/icons/Battery60";
import Battery20Icon from "@material-ui/icons/Battery20";
import BatteryAlertIcon from "@material-ui/icons/BatteryAlert";
import BatteryUnknownIcon from "@material-ui/icons/BatteryUnknown";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core";
import { deviceTrackingStyles } from "./DeviceTracking.styles";
import getTrackingStatusInfo, { TRACKER_STATUS } from "./getTrackingStatusInfo";
import BusMapDialog from "./DeviceBusMap";
import OperationalEventGisOverlay from "./OperationalEventGisOverlay";
import * as DATANETAPI from "../DataNet/DataNet";
import * as CONF from "../../constants/conf";

const useStyles = makeStyles(() => ({
    ...deviceTrackingStyles
}));

const DeviceTrackingStatusIcon = ({
    device,
    trackerPosition,
    showMapButton = false,
    statusCard = false,
    trackerUpdateDate,
    trackerOperationalGis,
    onRefresh,
    autoOpenOperationalGis = false,
    onOperationalGisAutoOpened,
    onClickOverride,
    enableInlineOperationalMap = false
}) => {

    const { t } = useTranslation();
    const classes = useStyles();
    const statusInfo = getTrackingStatusInfo(device?.tracker_status, t);
    const [mapOpen, setMapOpen] = useState(false);
    const [operationalMapOpen, setOperationalMapOpen] = useState(false);
    const [liveBusPosition, setLiveBusPosition] = useState(null);
    const busPositionRequestInFlight = useRef(false);

    const hasBusAssociation =
        device?.bus_id != null ||
        device?.busId != null ||
        device?.tracker_assignments?.bus != null;

    const isOperationalMapStatus =
        device?.tracker_status === TRACKER_STATUS.EXTRACTED ||
        device?.tracker_status === TRACKER_STATUS.NO_SIGNAL;

    const isOperationalActive =
        device?.tracker_status === TRACKER_STATUS.ACTIVE;

    const operationalMapButtonText =
        device?.tracker_status === TRACKER_STATUS.EXTRACTED
            ? "devices.tracking.config.status.see_event_map"
            : "devices.tracking.config.status.see_last_position";

    const canOpenMap =
        statusInfo.clickable &&
        hasBusAssociation;

    const canOpenOperationalInlineMap =
        enableInlineOperationalMap &&
        isOperationalMapStatus;


    const handleSilentRefresh = useCallback(() => {
        return onRefresh?.({ silent: true });
    }, [onRefresh]);

    useEffect(() => {
        if (!autoOpenOperationalGis || !statusCard) {
            return;
        }

        if (isOperationalMapStatus) {
            setOperationalMapOpen(true);
            onOperationalGisAutoOpened?.();
            return;
        }

        if (canOpenMap) {
            setMapOpen(true);
            onOperationalGisAutoOpened?.();
        }
    }, [
        autoOpenOperationalGis,
        statusCard,
        isOperationalMapStatus,
        canOpenMap,
        onOperationalGisAutoOpened
    ]);

    const refreshBusPosition = useCallback(async () => {
        if (!device?.id || busPositionRequestInFlight.current) {
            return null;
        }

        busPositionRequestInFlight.current = true;

        try {
            const response = await DATANETAPI.getBusPosition(device.id);

            if (response?.status !== 1) {
                return null;
            }

            const position = response.data ?? null;

            setLiveBusPosition(position);

            return position;
        } catch (error) {
            console.error(
                "Error obteniendo la posición del autobús",
                error
            );

            return null;
        } finally {
            busPositionRequestInFlight.current = false;
        }
    }, [device?.id]);

    const handleTrackingStatusClick = async () => {
        if (enableInlineOperationalMap && isOperationalMapStatus) {
            setOperationalMapOpen(true);
            return;
        }

        if (!canOpenMap) {
            return;
        }

        const position = await refreshBusPosition();

        const hasValidPosition =
            position?.latitude != null &&
            position?.longitude != null;

        if (!hasValidPosition) {
            return;
        }

        setMapOpen(true);
    };

    useEffect(() => {
        if (!mapOpen || !device?.id || !hasBusAssociation) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            refreshBusPosition();
        }, 60000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [
        mapOpen,
        device?.id,
        hasBusAssociation,
        refreshBusPosition
    ]);

    const trackerBattery = trackerOperationalGis?.last_tracker_battery;
    const trackerBatteryLevel = Number(trackerBattery?.level);
    const operationalIncidentStatus = trackerOperationalGis?.status;

    const isTrackerExtracted =
        device?.tracker_status === TRACKER_STATUS.EXTRACTED;

    const isTrackerNoSignal =
        device?.tracker_status === TRACKER_STATUS.NO_SIGNAL;

    const isOperationalClosurePending = [
        CONF.TRACKER_OPERATIONAL_INCIDENT_STATUS.PENDING_OPERATIONAL_CLOSURE,
        CONF.TRACKER_OPERATIONAL_INCIDENT_STATUS.NEEDS_MANUAL_CLOSURE,
        CONF.TRACKER_OPERATIONAL_INCIDENT_STATUS.READY_FOR_OPERATIONAL_CLOSURE
    ].includes(operationalIncidentStatus);

    const shouldShowTrackerBattery =
        isTrackerExtracted ||
        isTrackerNoSignal ||
        isOperationalClosurePending;

    const hasTrackerBattery =
        trackerBattery?.level !== null &&
        trackerBattery?.level !== undefined &&
        Number.isFinite(trackerBatteryLevel);

    const isTrackerBatteryDischarged =
        hasTrackerBattery && trackerBatteryLevel <= 1;

    const trackerBatteryLabel = !hasTrackerBattery
        ? t("devices.tracking.config.status.tracker_battery_not_available")
        : isTrackerBatteryDischarged
            ? t("devices.tracking.config.status.tracker_battery_discharged")
            : `${Math.round(trackerBatteryLevel)}%`;

    const trackerBatteryTitle = isTrackerNoSignal
        ? t("devices.tracking.config.status.tracker_battery_last_registered")
        : t("devices.tracking.config.status.tracker_battery");

    const trackerBatteryPresentation = !hasTrackerBattery
        ? {
            Icon: BatteryUnknownIcon,
            iconColor: "#78909C",
            textColor: "#607D8B",
            backgroundColor: "#ECEFF1"
        }
        : isTrackerBatteryDischarged
            ? {
                Icon: BatteryAlertIcon,
                iconColor: "#E53935",
                textColor: "#E53935",
                backgroundColor: "#FDEAEA"
            }
            : trackerBatteryLevel >= 60
                ? {
                    Icon: BatteryFullIcon,
                    iconColor: "#43A047",
                    textColor: "#2E7D32",
                    backgroundColor: "#EAF6EA"
                }
                : trackerBatteryLevel >= 20
                    ? {
                        Icon: Battery60Icon,
                        iconColor: "#F9A825",
                        textColor: "#8D6E00",
                        backgroundColor: "#FFF8E1"
                    }
                    : {
                        Icon: Battery20Icon,
                        iconColor: "#F4511E",
                        textColor: "#D84315",
                        backgroundColor: "#FBE9E7"
                    };

    const TrackerBatteryIcon = trackerBatteryPresentation.Icon;

    const icon = (
        <div className={classes.trackingWrapper} style={{ background: `${statusInfo.iconColor}15` }}>
            <DirectionsBusIcon
                className={classes[statusInfo.iconClass]}
                style={{ color: statusInfo.iconColor }}
            />
            {statusInfo.warning ? (
                <ReportProblemOutlinedIcon
                    className={classes.trackingWarningBadge}
                    style={{ color: statusInfo.iconColor }}
                />
            ) : statusInfo.error ? (
                <ErrorOutlineIcon
                    className={classes.trackingErrorBadge}
                    style={{ color: statusInfo.iconColor }}
                />
            ) : null}
        </div>
    );

    if (statusCard) {
        return (
            <>
                <div className={classes.trackingStatusCard}>
                    <div className={classes.trackingStatusBody}>
                        <div className={classes.trackingStatusIconContainer}>
                            {icon}
                        </div>

                        <div className={classes.trackingStatusInfo}>
                            <Typography
                                className={classes.trackingStatusTitle}
                                style={{ color: statusInfo.iconColor }}
                            >
                                {t(`devices.tracking.config.status.${statusInfo.translation}.modal`)}
                            </Typography>

                            <Typography className={classes.trackingStatusDate}>
                                {t("devices.tracking.config.status.tooltip.position.title")}
                                &nbsp;{"\u00B7"}&nbsp;
                                {trackerUpdateDate}
                            </Typography>

                            {shouldShowTrackerBattery && (
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        gap: 6,
                                        marginTop: 8
                                    }}
                                >
                                    <Chip
                                        size="small"
                                        icon={
                                            <TrackerBatteryIcon
                                                style={{
                                                    color: trackerBatteryPresentation.iconColor,
                                                    fontSize: 17
                                                }}
                                            />
                                        }
                                        label={`${trackerBatteryTitle} \u00B7 ${trackerBatteryLabel}`}
                                        style={{
                                            width: "fit-content",
                                            maxWidth: "100%",
                                            height: 24,
                                            borderRadius: 14,
                                            backgroundColor: trackerBatteryPresentation.backgroundColor,
                                            color: trackerBatteryPresentation.textColor,
                                            fontWeight: 500
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        <div className={classes.trackingStatusActions}>
                            {showMapButton && !isOperationalMapStatus && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    startIcon={<LocationOnOutlinedIcon />}
                                    onClick={handleTrackingStatusClick}
                                    disabled={!canOpenMap}
                                    className={classes.trackingMapButton}
                                >
                                    {t("devices.tracking.config.status.see_map")}
                                </Button>
                            )}

                            {statusCard && isOperationalMapStatus && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    startIcon={<LocationOnOutlinedIcon />}
                                    onClick={() => setOperationalMapOpen(true)}
                                    className={classes.trackingMapButton}
                                >
                                    {t(operationalMapButtonText)}
                                </Button>
                            )}

                            {onRefresh && (
                                <Button
                                    variant="text"
                                    size="small"
                                    startIcon={<RefreshIcon />}
                                    onClick={onRefresh}
                                    className={classes.trackingRefreshButton}
                                >
                                    {t("common.update")}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                <BusMapDialog
                    open={mapOpen}
                    onClose={() => setMapOpen(false)}
                    latitude={liveBusPosition?.latitude}
                    longitude={liveBusPosition?.longitude}
                    statusInfo={statusInfo}
                    useHeartMarker={isOperationalActive}
                />
                <OperationalEventGisOverlay
                    open={operationalMapOpen}
                    onClose={() => setOperationalMapOpen(false)}
                    trackerStatus={device?.tracker_status}
                    trackerOperationalGis={trackerOperationalGis}
                    busPosition={trackerPosition}
                    busStatusInfo={statusInfo}
                    device={device}
                    onRefresh={handleSilentRefresh}
                    t={t}
                />
            </>
        );
    }

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 8
            }}
        >
            <Tooltip title={statusInfo.text}>
                <span>
                    <IconButton
                        size="small"
                        disableRipple
                        style={{ background: "transparent" }}
                        disabled={!onClickOverride && !showMapButton && !canOpenMap && !canOpenOperationalInlineMap}
                        onClick={onClickOverride || (!showMapButton ? handleTrackingStatusClick : undefined)}
                    >
                        {icon}
                    </IconButton>
                </span>
            </Tooltip>

            {showMapButton && (
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ borderRadius: 8 }}
                    onClick={handleTrackingStatusClick}
                    disabled={!canOpenMap}
                >
                    {t("devices.tracking.config.status.see_map")}
                </Button>
            )}

            <BusMapDialog
                open={mapOpen}
                onClose={() => setMapOpen(false)}
                latitude={liveBusPosition?.latitude}
                longitude={liveBusPosition?.longitude}
                statusInfo={statusInfo}
                useHeartMarker={isOperationalActive}
            />

            {enableInlineOperationalMap && (
                <OperationalEventGisOverlay
                    open={operationalMapOpen}
                    onClose={() => setOperationalMapOpen(false)}
                    trackerStatus={device?.tracker_status}
                    trackerOperationalGis={trackerOperationalGis}
                    busPosition={trackerPosition}
                    busStatusInfo={statusInfo}
                    device={device}
                    onRefresh={handleSilentRefresh}
                    t={t}
                />
            )}
        </div>
    );
};

export default DeviceTrackingStatusIcon;
